/**
 * Client read model for HR CSV import (active import batch only).
 */
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where, type Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  HR_FY2569_FISCAL_YEAR,
  HR_FY2569_PUBLISHED_YEAR,
  HR_FY2569_UNITS,
} from '../data/hrFy2569Snapshot';
import { HR_JOB_SATISFACTION_HEADERS, HR_JOB_SATISFACTION_ROWS } from '../features/nso-public/hrJobSatisfaction';
import { isValidCanonicalDepartmentId } from '../lib/canonicalIds';
import { currentFiscalYearBe } from '../lib/reportingMonth';

export type HrValueStatus = 'valid' | 'zeroNeedsReview' | 'missing' | 'invalidFormula' | 'notApplicable';
export type HrMetricId = 'happinometer' | 'engagement' | 'jobSatisfaction' | 'turnoverRate';
export type HrOverviewSectionType =
  | 'monthlyStaffTypeCount'
  | 'resignationReason'
  | 'tenureBucket'
  | 'unitTurnoverCount'
  | 'monthlyTurnoverRate';
export type MetricDirection = 'higherIsBetter' | 'lowerIsBetter' | 'neutral';

export const HR_METRIC_DIRECTION: Record<string, MetricDirection> = {
  happinometer: 'higherIsBetter',
  engagement: 'higherIsBetter',
  jobSatisfaction: 'higherIsBetter',
  turnoverRate: 'lowerIsBetter',
};

export interface HrImportMeta {
  activeImportBatchId?: string;
  previousActiveImportBatchId?: string;
  syncStatus?: string;
  errorCode?: string;
  errorSummary?: string;
  lastSuccessfulSyncAt?: Timestamp | { toDate: () => Date } | null;
  lastFailedSyncAt?: Timestamp | { toDate: () => Date } | null;
  importSchemaVersion?: string;
  parserVersion?: string;
  unmappedUnitLabelCount?: number;
  unmappedUnitLabels?: string[];
  invalidFormulaCount?: number;
  zeroNeedsReviewCount?: number;
  skippedSummaryRows?: number;
  writtenRecordCount?: { annualMetrics?: number; turnoverAnnual?: number; turnoverOverview?: number };
  durationMs?: number;
}

export interface HrAnnualMetric {
  id: string;
  metricId: HrMetricId;
  metricLabelTh: string;
  fiscalYear: number;
  unitNameRaw: string;
  departmentId?: string | null;
  valuePercent: number | null;
  valueStatus: HrValueStatus;
  isZeroValue: boolean;
  validForDashboard: boolean;
  importBatchId: string;
}

export interface HrTurnoverAnnual {
  id: string;
  fiscalYear: number;
  valuePercent: number | null;
  valueStatus: HrValueStatus;
  importBatchId: string;
  /** Reported resignation count for a published year total. Not a formula input. */
  reportedResignations?: number;
}

export interface HrTurnoverOverviewSection {
  id: string;
  fiscalYear: number;
  sectionType: HrOverviewSectionType;
  rowLabelRaw: string;
  month: number | null;
  value: number | null;
  valuePercent: number | null;
  valueStatus: HrValueStatus;
  importBatchId: string;
}

export const MAX_ROWS = 3000;

/**
 * Analytics contract for this descriptive dashboard:
 * use only valid rows for means, rankings, YoY deltas, dispersion, and moving averages.
 * Count all statuses only in coverage/data-quality summaries, and never coerce missing values to zero.
 */
export const HR_ANALYTICS_CONTRACT_TH =
  'ใช้เฉพาะรายการสถานะถูกต้องในการคำนวณค่าเฉลี่ย การจัดอันดับ การเปลี่ยนแปลงจากปีก่อน การกระจายคะแนน และค่าเฉลี่ยเคลื่อนที่';

/** Latest ปีงบประมาณ that HR charts may show. October opens the next fiscal year. */
export function currentBuddhistYear(now: Date = new Date()): number {
  return currentFiscalYearBe(now);
}

export function toValueStatus(v: unknown): HrValueStatus {
  const s = typeof v === 'string' ? v : '';
  if (s === 'valid' || s === 'zeroNeedsReview' || s === 'missing' || s === 'invalidFormula' || s === 'notApplicable') {
    return s;
  }
  return 'missing';
}

export function hrValueStatusLabelTh(s: HrValueStatus): string {
  switch (s) {
    case 'valid':
      return 'ถูกต้อง';
    case 'missing':
      return 'ไม่มีข้อมูล';
    case 'zeroNeedsReview':
      return 'เป็นศูนย์ (รอตรวจสอบ)';
    case 'invalidFormula':
      return 'สูตรไม่ถูกต้อง';
    case 'notApplicable':
      return 'ไม่เกี่ยวข้อง';
  }
}

export function hrValueStatusExplanationTh(s: HrValueStatus): string {
  switch (s) {
    case 'valid':
      return 'ผ่านการตรวจสอบและใช้คำนวณตัวชี้วัดได้';
    case 'missing':
      return 'ไม่มีค่าที่นำเข้าในแหล่งข้อมูล';
    case 'zeroNeedsReview':
      return 'ค่าที่นำเข้าเป็นศูนย์และต้องตรวจสอบก่อนใช้สรุปผล';
    case 'invalidFormula':
      return 'สูตรจากแหล่งข้อมูลไม่ถูกต้องหรืออ่านค่าไม่ได้';
    case 'notApplicable':
      return 'รายการนี้ไม่เกี่ยวข้องกับตัวชี้วัด';
  }
}

export function formatPercent2(v: number | null): string {
  if (typeof v !== 'number' || Number.isNaN(v)) return 'ไม่มีข้อมูล';
  return `${v.toFixed(2)}%`;
}

export function formatHrPercentCell(args: { valuePercent: number | null; valueStatus: HrValueStatus }): string {
  if (args.valueStatus !== 'valid') return hrValueStatusLabelTh(args.valueStatus);
  return formatPercent2(args.valuePercent);
}

export function calculateValidMetricMean(
  rows: readonly HrAnnualMetric[],
  metricId: HrMetricId,
  fiscalYear: number,
): { valuePercent: number | null; n: number } {
  const validRows = rows.filter((r) => r.metricId === metricId && r.fiscalYear === fiscalYear && r.valueStatus === 'valid' && r.valuePercent != null);
  if (!validRows.length) return { valuePercent: null, n: 0 };
  const sum = validRows.reduce((total, row) => total + (row.valuePercent ?? 0), 0);
  return { valuePercent: sum / validRows.length, n: validRows.length };
}

const PUBLISHED_SENTIMENT_YEAR: Record<'happinometer' | 'engagement' | 'jobSatisfaction', number> = {
  happinometer: HR_FY2569_PUBLISHED_YEAR.happinometer,
  engagement: HR_FY2569_PUBLISHED_YEAR.engagement,
  jobSatisfaction: HR_FY2569_PUBLISHED_YEAR.jobSatisfaction,
};

/** Year headline for the HR cards and trend. Fiscal year 2569 uses the reported total. */
export function officialSentimentYear(
  rows: readonly HrAnnualMetric[],
  metricId: 'happinometer' | 'engagement' | 'jobSatisfaction',
  fiscalYear: number,
): { valuePercent: number | null; n: number; published: boolean } {
  if (fiscalYear === HR_FY2569_FISCAL_YEAR) {
    return { valuePercent: PUBLISHED_SENTIMENT_YEAR[metricId], n: 0, published: true };
  }
  return { ...calculateValidMetricMean(rows, metricId, fiscalYear), published: false };
}

export function publishedFy2569Turnover(importBatchId: string): HrTurnoverAnnual {
  return {
    id: `turnoverRate|${HR_FY2569_FISCAL_YEAR}`,
    fiscalYear: HR_FY2569_FISCAL_YEAR,
    valuePercent: HR_FY2569_PUBLISHED_YEAR.turnoverRate,
    valueStatus: 'valid',
    importBatchId,
    reportedResignations: HR_FY2569_PUBLISHED_YEAR.turnoverResignations,
  };
}

export function withPublishedHrTurnover(
  turnover: readonly HrTurnoverAnnual[],
  importBatchId: string,
): HrTurnoverAnnual[] {
  return [
    ...turnover.filter((row) => row.fiscalYear !== HR_FY2569_FISCAL_YEAR),
    publishedFy2569Turnover(importBatchId),
  ];
}

export function calculatePercentagePointDelta(current: number | null, previous: number | null): number | null {
  if (current == null || previous == null) return null;
  return current - previous;
}

export type HrStatusCounts = Record<HrValueStatus, number>;

export interface HrCoverageSummary {
  fiscalYear: number;
  metricId: HrMetricId;
  counts: HrStatusCounts;
  total: number;
  valid: number;
  coveragePercent: number | null;
}

export interface HrUnitRankings {
  highest: HrAnnualMetric[];
  lowest: HrAnnualMetric[];
}

export interface HrDepartmentSummary {
  departmentId: string | null;
  label: string;
  meanPercent: number;
  medianPercent: number;
  n: number;
}

export interface HrDispersionSummary {
  fiscalYear: number;
  metricId: HrMetricId;
  n: number;
  min: number | null;
  q1: number | null;
  median: number | null;
  q3: number | null;
  iqr: number | null;
  max: number | null;
}

export interface HrMonthlyTurnoverPoint {
  month: number;
  valuePercent: number;
  movingAverage3: number | null;
}

const EMPTY_STATUS_COUNTS: HrStatusCounts = {
  valid: 0,
  missing: 0,
  zeroNeedsReview: 0,
  invalidFormula: 0,
  notApplicable: 0,
};

function validMetricValues(rows: readonly HrAnnualMetric[], metricId: HrMetricId, fiscalYear: number): HrAnnualMetric[] {
  return rows.filter((r) => r.metricId === metricId && r.fiscalYear === fiscalYear && r.valueStatus === 'valid' && r.valuePercent != null);
}

function median(values: readonly number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid] ?? null;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

function quartiles(values: readonly number[]): { q1: number | null; medianValue: number | null; q3: number | null } {
  if (!values.length) return { q1: null, medianValue: null, q3: null };
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const lower = sorted.length % 2 === 0 ? sorted.slice(0, mid) : sorted.slice(0, mid);
  const upper = sorted.length % 2 === 0 ? sorted.slice(mid) : sorted.slice(mid + 1);
  return {
    q1: median(lower),
    medianValue: median(sorted),
    q3: median(upper),
  };
}

export function countByStatus(rows: readonly HrAnnualMetric[], args: { metricId: HrMetricId; fiscalYear: number }): HrCoverageSummary {
  const counts: HrStatusCounts = { ...EMPTY_STATUS_COUNTS };
  const filtered = rows.filter((r) => r.metricId === args.metricId && r.fiscalYear === args.fiscalYear);
  for (const row of filtered) {
    counts[row.valueStatus] += 1;
  }
  const total = filtered.length;
  const valid = counts.valid;
  return {
    fiscalYear: args.fiscalYear,
    metricId: args.metricId,
    counts,
    total,
    valid,
    coveragePercent: total > 0 ? (valid / total) * 100 : null,
  };
}

export function rankUnits(rows: readonly HrAnnualMetric[], args: { metricId: HrMetricId; fiscalYear: number; limit: number }): HrUnitRankings {
  const validRows = validMetricValues(rows, args.metricId, args.fiscalYear);
  const byValueDesc = [...validRows].sort((a, b) => {
    const diff = (b.valuePercent ?? 0) - (a.valuePercent ?? 0);
    return diff !== 0 ? diff : a.unitNameRaw.localeCompare(b.unitNameRaw, 'th');
  });
  const byValueAsc = [...validRows].sort((a, b) => {
    const diff = (a.valuePercent ?? 0) - (b.valuePercent ?? 0);
    return diff !== 0 ? diff : a.unitNameRaw.localeCompare(b.unitNameRaw, 'th');
  });
  return {
    highest: byValueDesc.slice(0, args.limit),
    lowest: byValueAsc.slice(0, args.limit),
  };
}

export function groupByDepartment(rows: readonly HrAnnualMetric[], args: { metricId: HrMetricId; fiscalYear: number }): HrDepartmentSummary[] {
  const groups = new Map<string, { departmentId: string | null; label: string; values: number[] }>();
  for (const row of validMetricValues(rows, args.metricId, args.fiscalYear)) {
    const departmentId = row.departmentId?.trim() || null;
    const key = departmentId ?? '__unknown__';
    const current = groups.get(key) ?? {
      departmentId,
      label: departmentId ?? 'ไม่ทราบแผนก',
      values: [],
    };
    current.values.push(row.valuePercent ?? 0);
    groups.set(key, current);
  }
  return [...groups.values()]
    .map((group) => {
      const sum = group.values.reduce((total, value) => total + value, 0);
      return {
        departmentId: group.departmentId,
        label: group.label,
        meanPercent: sum / group.values.length,
        medianPercent: median(group.values) ?? 0,
        n: group.values.length,
      };
    })
    .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label, 'th'));
}

export function computeDispersion(rows: readonly HrAnnualMetric[], args: { metricId: HrMetricId; fiscalYear: number }): HrDispersionSummary {
  const values = validMetricValues(rows, args.metricId, args.fiscalYear)
    .map((row) => row.valuePercent)
    .filter((value): value is number => value != null)
    .sort((a, b) => a - b);
  const q = quartiles(values);
  return {
    fiscalYear: args.fiscalYear,
    metricId: args.metricId,
    n: values.length,
    min: values[0] ?? null,
    q1: q.q1,
    median: q.medianValue,
    q3: q.q3,
    iqr: q.q1 != null && q.q3 != null ? q.q3 - q.q1 : null,
    max: values[values.length - 1] ?? null,
  };
}

export function monthlyTurnoverSeries(rows: readonly HrTurnoverOverviewSection[]): HrMonthlyTurnoverPoint[] {
  const validRows = rows
    .filter((row) => row.sectionType === 'monthlyTurnoverRate' && row.valueStatus === 'valid' && row.month != null && row.valuePercent != null)
    .sort((a, b) => (a.month ?? 0) - (b.month ?? 0));
  return validRows.map((row, index) => {
    const windowRows = validRows.slice(index - 2, index + 1);
    const movingAverage3 =
      windowRows.length === 3
        ? windowRows.reduce((total, item) => total + (item.valuePercent ?? 0), 0) / 3
        : null;
    return {
      month: row.month ?? 0,
      valuePercent: row.valuePercent ?? 0,
      movingAverage3,
    };
  });
}

function toMetricId(v: unknown): HrMetricId | null {
  const s = typeof v === 'string' ? v : '';
  if (s === 'happinometer' || s === 'engagement' || s === 'jobSatisfaction' || s === 'turnoverRate') return s;
  return null;
}

function toSectionType(v: unknown): HrOverviewSectionType | null {
  const s = typeof v === 'string' ? v : '';
  const ok: HrOverviewSectionType[] = [
    'monthlyStaffTypeCount',
    'resignationReason',
    'tenureBucket',
    'unitTurnoverCount',
    'monthlyTurnoverRate',
  ];
  return (ok as string[]).includes(s) ? (s as HrOverviewSectionType) : null;
}

export function parseHrImportMeta(d: Record<string, unknown>): HrImportMeta {
  return {
    activeImportBatchId: typeof d.activeImportBatchId === 'string' ? d.activeImportBatchId : undefined,
    previousActiveImportBatchId: typeof d.previousActiveImportBatchId === 'string' ? d.previousActiveImportBatchId : undefined,
    syncStatus: typeof d.syncStatus === 'string' ? d.syncStatus : undefined,
    errorCode: typeof d.errorCode === 'string' ? d.errorCode : undefined,
    errorSummary: typeof d.errorSummary === 'string' ? d.errorSummary : undefined,
    lastSuccessfulSyncAt: d.lastSuccessfulSyncAt as HrImportMeta['lastSuccessfulSyncAt'],
    lastFailedSyncAt: d.lastFailedSyncAt as HrImportMeta['lastFailedSyncAt'],
    importSchemaVersion: typeof d.importSchemaVersion === 'string' ? d.importSchemaVersion : undefined,
    parserVersion: typeof d.parserVersion === 'string' ? d.parserVersion : undefined,
    unmappedUnitLabelCount: typeof d.unmappedUnitLabelCount === 'number' ? d.unmappedUnitLabelCount : undefined,
    unmappedUnitLabels: Array.isArray(d.unmappedUnitLabels) ? (d.unmappedUnitLabels as string[]) : undefined,
    invalidFormulaCount: typeof d.invalidFormulaCount === 'number' ? d.invalidFormulaCount : undefined,
    zeroNeedsReviewCount: typeof d.zeroNeedsReviewCount === 'number' ? d.zeroNeedsReviewCount : undefined,
    skippedSummaryRows: typeof d.skippedSummaryRows === 'number' ? d.skippedSummaryRows : undefined,
    writtenRecordCount:
      d.writtenRecordCount && typeof d.writtenRecordCount === 'object'
        ? (d.writtenRecordCount as HrImportMeta['writtenRecordCount'])
        : undefined,
    durationMs: typeof d.durationMs === 'number' ? d.durationMs : undefined,
  };
}

export async function getHrImportMeta(): Promise<HrImportMeta | null> {
  const snap = await getDoc(doc(db, 'hrImportMeta', 'summary'));
  if (!snap.exists()) return null;
  return parseHrImportMeta(snap.data() as Record<string, unknown>);
}

export interface HrImportDashboardBundle {
  meta: HrImportMeta | null;
  activeBatchId: string | null;
  annual: HrAnnualMetric[];
  turnover: HrTurnoverAnnual[];
  overview: HrTurnoverOverviewSection[];
}

export const PUBLISHED_HR_SNAPSHOT_BATCH_ID = 'published-fy2569';

export function isHrImportBundleCapped(bundle: Pick<HrImportDashboardBundle, 'annual' | 'overview'>): boolean {
  return bundle.annual.length >= MAX_ROWS || bundle.overview.length >= MAX_ROWS;
}

/** True when the page is showing the coded fiscal-year snapshot instead of an import batch. */
export function bundleUsesPublishedHrSnapshot(
  bundle: Pick<HrImportDashboardBundle, 'activeBatchId' | 'annual'>,
): boolean {
  if (bundle.activeBatchId) return false;
  return bundle.annual.some((row) => row.importBatchId === PUBLISHED_HR_SNAPSHOT_BATCH_ID);
}

function parsePublishedJobSatCell(raw: string): Pick<HrAnnualMetric, 'valuePercent' | 'valueStatus' | 'isZeroValue' | 'validForDashboard'> {
  const cellRaw = raw.trim();
  if (!cellRaw) {
    return { valuePercent: null, valueStatus: 'missing', isZeroValue: false, validForDashboard: false };
  }
  const n = Number(cellRaw.replace(/,/g, ''));
  if (!Number.isFinite(n)) {
    return { valuePercent: null, valueStatus: 'missing', isZeroValue: false, validForDashboard: false };
  }
  if (n === 0) {
    return { valuePercent: 0, valueStatus: 'zeroNeedsReview', isZeroValue: true, validForDashboard: false };
  }
  return { valuePercent: n, valueStatus: 'valid', isZeroValue: false, validForDashboard: true };
}

/** Published Job sat table replaces the imported sheet rows on the HR dashboard. */
export function publishedJobSatisfactionMetrics(importBatchId: string): HrAnnualMetric[] {
  const yearCols = HR_JOB_SATISFACTION_HEADERS.map((header, index) => ({ index, year: Number(header) })).filter(
    (col) => col.index >= 2 && Number.isInteger(col.year) && col.year !== HR_FY2569_FISCAL_YEAR,
  );
  const rows: HrAnnualMetric[] = [];
  for (const source of HR_JOB_SATISFACTION_ROWS) {
    const unitNameRaw = source[0] ?? '';
    for (const col of yearCols) {
      const parsed = parsePublishedJobSatCell(source[col.index] ?? '');
      rows.push({
        id: `jobSatisfaction|${unitNameRaw}|${col.year}`,
        metricId: 'jobSatisfaction',
        metricLabelTh: 'ความพึงพอใจในงาน',
        fiscalYear: col.year,
        unitNameRaw,
        departmentId: isValidCanonicalDepartmentId(unitNameRaw) ? unitNameRaw : null,
        ...parsed,
        importBatchId,
      });
    }
  }
  return rows;
}

export function withPublishedJobSatisfaction(annual: readonly HrAnnualMetric[], importBatchId: string): HrAnnualMetric[] {
  return [...annual.filter((row) => row.metricId !== 'jobSatisfaction'), ...publishedJobSatisfactionMetrics(importBatchId)];
}

const FY2569_METRIC_LABEL = {
  happinometer: 'ดัชนีความสุข',
  jobSatisfaction: 'ความพึงพอใจในงาน',
  engagement: 'ความผูกพัน',
} as const;

/** Code snapshot for fiscal year 2569 happiness, organizational satisfaction, and engagement. */
export function publishedFy2569Metrics(importBatchId: string): HrAnnualMetric[] {
  const rows: HrAnnualMetric[] = [];
  for (const unit of HR_FY2569_UNITS) {
    const departmentId = isValidCanonicalDepartmentId(unit.unitNameRaw) ? unit.unitNameRaw : null;
    const metrics = [
      ['happinometer', unit.happinometer],
      ['jobSatisfaction', unit.jobSatisfaction],
      ['engagement', unit.engagement],
    ] as const;
    for (const [metricId, valuePercent] of metrics) {
      rows.push({
        id: `${metricId}|${unit.unitNameRaw}|${HR_FY2569_FISCAL_YEAR}`,
        metricId,
        metricLabelTh: FY2569_METRIC_LABEL[metricId],
        fiscalYear: HR_FY2569_FISCAL_YEAR,
        unitNameRaw: unit.unitNameRaw,
        departmentId,
        valuePercent,
        valueStatus: 'valid',
        isZeroValue: false,
        validForDashboard: true,
        importBatchId,
      });
    }
  }
  return rows;
}

/** Keeps 2566–2568 Job sat from the public archive and replaces 2569 sentiment with the snapshot. */
export function withPublishedHrDashboardMetrics(annual: readonly HrAnnualMetric[], importBatchId: string): HrAnnualMetric[] {
  const published = withPublishedJobSatisfaction(annual, importBatchId).filter(
    (row) =>
      !(
        row.fiscalYear === HR_FY2569_FISCAL_YEAR &&
        (row.metricId === 'happinometer' || row.metricId === 'engagement' || row.metricId === 'jobSatisfaction')
      ),
  );
  return [...published, ...publishedFy2569Metrics(importBatchId)];
}

/** Flat scores only. No import batch, turnover sheet, or sync summary. */
export function publishedHrDashboardBundle(): HrImportDashboardBundle {
  return {
    meta: null,
    activeBatchId: null,
    annual: withPublishedHrDashboardMetrics([], PUBLISHED_HR_SNAPSHOT_BATCH_ID),
    turnover: withPublishedHrTurnover([], PUBLISHED_HR_SNAPSHOT_BATCH_ID),
    overview: [],
  };
}

export async function fetchHrImportDashboardBundle(): Promise<HrImportDashboardBundle> {
  const meta = await getHrImportMeta();
  const activeBatchId = meta?.activeImportBatchId?.trim() || null;
  if (!activeBatchId) {
    return {
      meta,
      activeBatchId: null,
      annual: publishedFy2569Metrics(PUBLISHED_HR_SNAPSHOT_BATCH_ID),
      turnover: withPublishedHrTurnover([], PUBLISHED_HR_SNAPSHOT_BATCH_ID),
      overview: [],
    };
  }
  const [annual, turnover, overview] = await Promise.all([
    listHrAnnualMetrics(activeBatchId),
    listHrTurnoverAnnual(activeBatchId),
    listHrTurnoverOverviewSections(activeBatchId),
  ]);
  return {
    meta,
    activeBatchId,
    annual: withPublishedHrDashboardMetrics(annual, activeBatchId),
    turnover: withPublishedHrTurnover(turnover, activeBatchId),
    overview,
  };
}

export async function listHrAnnualMetrics(importBatchId: string, metricId?: HrMetricId): Promise<HrAnnualMetric[]> {
  const coll = collection(db, 'hrAnnualMetrics');
  const qy =
    metricId != null
      ? query(
          coll,
          where('importBatchId', '==', importBatchId),
          where('metricId', '==', metricId),
          orderBy('fiscalYear'),
          limit(MAX_ROWS),
        )
      : query(coll, where('importBatchId', '==', importBatchId), limit(MAX_ROWS));
  const snap = await getDocs(qy);
  const out: HrAnnualMetric[] = [];
  snap.forEach((s) => {
    const d = s.data() as Record<string, unknown>;
    const mid = toMetricId(d.metricId);
    if (!mid || mid === 'turnoverRate') return;
    out.push({
      id: s.id,
      metricId: mid,
      metricLabelTh: typeof d.metricLabelTh === 'string' ? d.metricLabelTh : mid,
      fiscalYear: typeof d.fiscalYear === 'number' ? d.fiscalYear : 0,
      unitNameRaw: typeof d.unitNameRaw === 'string' ? d.unitNameRaw : '',
      departmentId: typeof d.departmentId === 'string' ? d.departmentId : null,
      valuePercent: typeof d.valuePercent === 'number' ? d.valuePercent : d.valuePercent === null ? null : null,
      valueStatus: toValueStatus(d.valueStatus),
      isZeroValue: d.isZeroValue === true,
      validForDashboard: d.validForDashboard === true,
      importBatchId: typeof d.importBatchId === 'string' ? d.importBatchId : importBatchId,
    });
  });
  out.sort((a, b) => a.fiscalYear - b.fiscalYear || a.unitNameRaw.localeCompare(b.unitNameRaw, 'th'));
  return out;
}

export async function listHrTurnoverAnnual(importBatchId: string): Promise<HrTurnoverAnnual[]> {
  const coll = collection(db, 'hrTurnoverAnnual');
  const qy = query(coll, where('importBatchId', '==', importBatchId), orderBy('fiscalYear'), limit(200));
  const snap = await getDocs(qy);
  const out: HrTurnoverAnnual[] = [];
  snap.forEach((s) => {
    const d = s.data() as Record<string, unknown>;
    out.push({
      id: s.id,
      fiscalYear: typeof d.fiscalYear === 'number' ? d.fiscalYear : 0,
      valuePercent: typeof d.valuePercent === 'number' ? d.valuePercent : null,
      valueStatus: toValueStatus(d.valueStatus),
      importBatchId: typeof d.importBatchId === 'string' ? d.importBatchId : importBatchId,
    });
  });
  return out;
}

export async function listHrTurnoverOverviewSections(
  importBatchId: string,
  sectionType?: HrOverviewSectionType,
): Promise<HrTurnoverOverviewSection[]> {
  const coll = collection(db, 'hrTurnoverOverviewSections');
  const qy =
    sectionType != null
      ? query(
          coll,
          where('importBatchId', '==', importBatchId),
          where('sectionType', '==', sectionType),
          limit(MAX_ROWS),
        )
      : query(coll, where('importBatchId', '==', importBatchId), limit(MAX_ROWS));
  const snap = await getDocs(qy);
  const out: HrTurnoverOverviewSection[] = [];
  snap.forEach((s) => {
    const d = s.data() as Record<string, unknown>;
    const st = toSectionType(d.sectionType);
    if (!st) return;
    out.push({
      id: s.id,
      fiscalYear: typeof d.fiscalYear === 'number' ? d.fiscalYear : 2568,
      sectionType: st,
      rowLabelRaw: typeof d.rowLabelRaw === 'string' ? d.rowLabelRaw : '',
      month: typeof d.month === 'number' ? d.month : null,
      value: typeof d.value === 'number' ? d.value : null,
      valuePercent: typeof d.valuePercent === 'number' ? d.valuePercent : null,
      valueStatus: toValueStatus(d.valueStatus),
      importBatchId: typeof d.importBatchId === 'string' ? d.importBatchId : importBatchId,
    });
  });
  return out;
}

/** Aggregate: mean of valid `valuePercent` per metric and fiscal year (raw fallback; not a statistical claim). */
export function aggregateSentimentByYear(
  rows: HrAnnualMetric[],
  metricId: HrMetricId,
): { fiscalYear: number; meanPercent: number | null; n: number }[] {
  const byYear = new Map<number, { sum: number; n: number }>();
  for (const r of rows) {
    if (r.metricId !== metricId) continue;
    if (r.valueStatus !== 'valid' || r.valuePercent == null) continue;
    const cur = byYear.get(r.fiscalYear) ?? { sum: 0, n: 0 };
    cur.sum += r.valuePercent;
    cur.n += 1;
    byYear.set(r.fiscalYear, cur);
  }
  return [...byYear.entries()]
    .map(([fiscalYear, { sum, n }]) => {
      const published = fiscalYear === HR_FY2569_FISCAL_YEAR ? PUBLISHED_SENTIMENT_YEAR[metricId as keyof typeof PUBLISHED_SENTIMENT_YEAR] : undefined;
      if (published != null) {
        return { fiscalYear, meanPercent: published, n: 0 };
      }
      return {
        fiscalYear,
        meanPercent: n > 0 ? sum / n : null,
        n,
      };
    })
    .sort((a, b) => a.fiscalYear - b.fiscalYear);
}

export function latestTurnoverRate(turnover: HrTurnoverAnnual[], now: Date = new Date()): HrTurnoverAnnual | null {
  if (!turnover.length) return null;
  const maxYear = currentBuddhistYear(now);
  const pool = turnover.filter((r) => r.fiscalYear > 0 && r.fiscalYear <= maxYear);
  const validPool = pool.filter((r) => r.valueStatus === 'valid' && r.valuePercent != null);
  const pickFrom = validPool.length ? validPool : pool.length ? pool : turnover;
  return [...pickFrom].sort((a, b) => b.fiscalYear - a.fiscalYear)[0] ?? null;
}
